import { Card } from 'components/ui/card';
import React from 'react';

const Privacy: React.FC = () => {
  return (
    <Card maxWidth="none">
      <main className="flex justify-center">
        <article
          aria-labelledby="privacy-policy-title"
          className="w-full space-y-6 p-3 text-sm leading-relaxed text-muted-foreground sm:p-6 sm:text-base"
        >
          <header className="space-y-3">
            <div className="flex flex-wrap items-baseline gap-3">
              <h1 id="privacy-policy-title" className="text-2xl font-semibold text-foreground sm:text-3xl">
                Privacy Policy
              </h1>
              <span className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground sm:text-sm">
                Effective: March 2026
              </span>
            </div>

            <p className="text-xs leading-relaxed sm:text-sm">
              <strong className="text-foreground">Website:</strong>{' '}
              <a
                href="https://wisefriend.org"
                rel="noopener"
                className="text-primary underline-offset-4 hover:underline"
              >
                wisefriend.org
              </a>{' '}
              <span aria-hidden="true">&middot;</span>{' '}
              <strong className="text-foreground">Business:</strong> Wise Friend{' '}
              <span aria-hidden="true">&middot;</span>{' '}
              <strong className="text-foreground">Location:</strong> Nevada, United States
            </p>
          </header>

          <section className="space-y-6">
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground sm:text-xl">1. Overview</h2>
              <p>
                Wise Friend is a behavioral health skills platform designed to help people better understand
                themselves, build practical life skills, and support others.
              </p>
              <p>This policy explains:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>what we collect</li>
                <li>how we use it</li>
                <li>who we share it with</li>
                <li>what choices you have</li>
              </ul>
              <p>We only collect what we need to operate and improve the platform.</p>
            </div>

            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-foreground sm:text-xl">2. Information We Collect</h2>

              <div className="space-y-2">
                <h3 className="font-semibold text-foreground">A. Account Information</h3>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Name</li>
                  <li>Email</li>
                  <li>Phone number (if provided)</li>
                  <li>Login credentials (securely stored)</li>
                </ul>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-foreground">B. User Content</h3>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Responses to exercises, reflections, and surveys</li>
                  <li>Notes, journal entries, and course inputs</li>
                  <li>Messages or interactions within the platform</li>
                </ul>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-foreground">C. Program &amp; Survey Data (Important)</h3>
                <p>If you participate in a program, we may collect:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>survey responses</li>
                  <li>progress data</li>
                  <li>engagement metrics</li>
                </ul>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-foreground">D. Technical &amp; Usage Data</h3>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Device type, browser, IP address</li>
                  <li>Pages visited, time spent, interactions</li>
                  <li>General analytics data</li>
                </ul>
              </div>
            </div>

            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground sm:text-xl">3. How We Use Information</h2>
              <p>We use your information to:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>operate and improve the platform</li>
                <li>personalize your experience</li>
                <li>deliver courses, tools, and mentorship experiences</li>
                <li>send service-related communications</li>
                <li>analyze usage and improve outcomes</li>
              </ul>
              <p>We do not sell your personal data.</p>
            </div>

            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-foreground sm:text-xl">
                4. Data Sharing with Program Partners
              </h2>
              <p>
                Some Wise Friend experiences are delivered <strong className="text-foreground">in partnership with organizations</strong>.
              </p>
              <p>If you participate in one of these programs:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>
                  Your <strong className="text-foreground">survey responses, engagement data, and program progress</strong>{' '}
                  may be shared with that organization
                </li>
                <li>
                  This may include <strong className="text-foreground">aggregated or individual-level data</strong>,
                  depending on the program design
                </li>
              </ul>
              <p>This data is used to:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>measure program effectiveness</li>
                <li>improve training outcomes</li>
                <li>support participant development</li>
              </ul>
              <p>
                By participating in a partner-sponsored program, you <strong className="text-foreground">consent to this data sharing</strong>.
              </p>
              <p>We aim to limit shared data to what is necessary and appropriate for the program.</p>
            </div>

            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground sm:text-xl">5. AI and Data Processing</h2>
              <p>Some features use AI (e.g., reflections, summaries, guidance).</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>Your inputs may be processed by third-party AI providers (e.g., OpenAI)</li>
                <li>Data is used only to generate responses and improve functionality</li>
                <li>We do not use your personal content to train external models unless explicitly stated</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground sm:text-xl">6. Communications (Email &amp; SMS)</h2>
              <p>By providing your contact information, you agree to receive:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>account and program updates</li>
                <li>reminders and administrative messages</li>
              </ul>
              <p>SMS:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>message frequency varies</li>
                <li>message/data rates may apply</li>
                <li>reply STOP to opt out</li>
              </ul>
              <p>We do not send marketing messages unless you opt in.</p>
            </div>

            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground sm:text-xl">7. Third-Party Services</h2>
              <p>We use trusted providers for:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>hosting (AWS, DigitalOcean)</li>
                <li>analytics</li>
                <li>AI processing</li>
              </ul>
              <p>These providers only access data necessary to perform their services.</p>
            </div>

            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground sm:text-xl">8. Data Storage &amp; Security</h2>
              <p>We use reasonable safeguards to protect your information.</p>
              <p>However, no system is 100% secure. You use the platform at your own risk.</p>
            </div>

            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground sm:text-xl">9. Your Rights &amp; Choices</h2>
              <p>You can:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>update or delete your account</li>
                <li>manage communication preferences</li>
                <li>request access or deletion of your data</li>
              </ul>
              <p>
                Contact:{' '}
                <a href="mailto:support@wisefriend.org" className="text-primary underline-offset-4 hover:underline">
                  support@wisefriend.org
                </a>
              </p>
            </div>

            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground sm:text-xl">10. California Privacy Rights</h2>
              <p>California residents may request:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>access to personal data</li>
                <li>deletion of personal data</li>
              </ul>
              <p>We will verify requests as required by law.</p>
            </div>

            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground sm:text-xl">11. Children</h2>
              <p>Wise Friend is not intended for children under 13.</p>
            </div>

            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground sm:text-xl">12. Changes</h2>
              <p>We may update this policy. Continued use = acceptance of updates.</p>
            </div>

            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground sm:text-xl">13. Contact</h2>
              <p>
                <a href="mailto:support@wisefriend.org" className="text-primary underline-offset-4 hover:underline">
                  support@wisefriend.org
                </a>
              </p>
            </div>
          </section>

          <p className="text-xs text-muted-foreground sm:text-sm">Last updated: March 2026</p>
        </article>
      </main>
    </Card>
  );
};

export default Privacy;
